import React, { useState } from 'react';
import axios from 'axios';
import { Department } from '../../types';

interface CreateDepartmentProps {
  organizationId: string;
  departments: Department[];
  onDepartmentCreated: () => void;
}

export default function CreateDepartment({ organizationId, departments, onDepartmentCreated }: CreateDepartmentProps) {
  const [name, setName] = useState('');
  const [parentDepartmentId, setParentDepartmentId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await axios.post(`/api/organizations/${organizationId}/departments`, {
        name,
        parent_department_id: parentDepartmentId || null
      });
      
      setName('');
      setParentDepartmentId('');
      onDepartmentCreated();
      
      // Emit event to notify other components
      window.dispatchEvent(new CustomEvent('departmentChanged'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create department');
    } finally {
      setIsLoading(false);
    }
  };

  // Recursive function to build department options with proper indentation
  const buildDepartmentOptions = (deps: Department[], level = 0): JSX.Element[] => {
    return deps.map(dept => [
      <option key={dept.id} value={dept.id}>
        {'  '.repeat(level) + dept.name}
      </option>,
      ...(dept.sub_departments ? buildDepartmentOptions(dept.sub_departments, level + 1) : [])
    ]).flat();
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4">Create New Department</h3>
      
      {error && (
        <div className="mb-4 text-red-500 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Department Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label htmlFor="parentDepartment" className="block text-sm font-medium text-gray-700">
            Parent Department (Optional)
          </label>
          <select
            id="parentDepartment"
            value={parentDepartmentId}
            onChange={(e) => setParentDepartmentId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">None (Top Level)</option>
            {buildDepartmentOptions(departments)}
          </select>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isLoading ? 'Creating...' : 'Create Department'}
        </button>
      </form>
    </div>
  );
} 